import z from 'zod'
import { ContentfulAssetSchema, ContentfulBynderImageSchema } from './asset'
import {
  ContentfulAltoTokenBackgroundSchema,
  ContentfulAltoTokenVariantSchema,
  ContentfulRichTextDocumentSchema,
} from './common'

export const ContentfulBadgeSchema = z.object({
  label: z.string().optional(),
  variant: ContentfulAltoTokenVariantSchema.optional(),
  contentType: z.literal('component-badge'),
})

export type ContentfulBadge = z.infer<typeof ContentfulBadgeSchema>

export const ContentfulCtaSchema = z.object({
  label: z.string(),
  link: z.string(),
  iconName: z.string().optional(),
  variant: z.enum(['primary', 'secondary', 'tertiary', 'quaternary', 'link']).optional(),
  contentType: z.literal('component-cta'),
})

export type ContentfulCta = z.infer<typeof ContentfulCtaSchema>

export const ContentfulCardSchema = z.object({
  identifier: z.string().optional(),
  heading: z.string().optional(),
  subheading: z.string().optional(),
  label: z.string().optional(),
  priceAmount: z.number().optional(),
  collectAmount: z.number().int().optional(),
  image: ContentfulAssetSchema.optional(),
  bynderImage: z.array(ContentfulBynderImageSchema).optional(),
  imageBadge: ContentfulBadgeSchema.optional(),
  background: ContentfulAltoTokenBackgroundSchema.optional().transform((val) => {
    if (val === 'base' || !val) {
      return val
    }
    // temporary for mvp of homepage, update this and the card component to get the correct background
    return 'accentSubtle'
  }),
  ctaLink: ContentfulCtaSchema.optional(),
  contentType: z.literal('component-card'),
  // unused but could be useful for flights
  destinationHeading: z.array(z.string()).length(2).optional(),
  destinationIataCodes: z.array(z.string()).length(2).optional(),
  origin: z.string().optional(),
  destination: z.string().optional(),
})

export type ContentfulCard = z.infer<typeof ContentfulCardSchema>

export const AlignmentEnum = z.enum(['left', 'center', 'right'])

export type Alignment = z.infer<typeof AlignmentEnum>

export const ContentfulEducationalComponentSchema = z.object({
  identifier: z.string().optional(),
  heading: z.string().optional(),
  headingAlignment: AlignmentEnum.optional(),
  items: z
    .array(
      z.object({
        icon: ContentfulAssetSchema.optional(),
        text: z.string().optional(),
        textAlignment: AlignmentEnum.optional(),
      })
    )
    .optional(),
  contentType: z.literal('componentEducational'),
})

export type ContentfulEducationalComponent = z.infer<typeof ContentfulEducationalComponentSchema>

export const ContentfulAccordionItemSchema = z.object({
  accordionButton: z.string().optional(),
  accordionPanel: ContentfulRichTextDocumentSchema.optional(),
})

export const ContentfulAccordionSectionSchema = z.object({
  identifier: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  allowMultipleExpanded: z.boolean().optional(),
  items: z.array(ContentfulAccordionItemSchema).optional(),
  contentType: z.literal('componentAccordion'),
})

export type ContentfulAccordionSection = z.infer<typeof ContentfulAccordionSectionSchema>
